import { Maximize2, Minimize2 } from "lucide-react";
import type React from "react";
import { useState } from "react";
import type { GraphViewMode } from "@/features/graph/types/graph-ui";
import GraphViewModeTabs from "./GraphViewModeTabs";
import HierarchyView from "./HierarchyView";
import WorkspaceView from "./WorkspaceView";

interface CenterGraphPanelProps {
	onKgSelect?: (graphId: string) => void;
	onNodeSelect?: (nodeId: string) => void;
	workspaceId?: string | null;
	/** When true, the first KG in the workspace view is expanded on load. */
	expanded?: boolean;
}

const CenterGraphPanel: React.FC<CenterGraphPanelProps> = ({
	onKgSelect,
	onNodeSelect,
	workspaceId,
	expanded = false,
}) => {
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [viewMode, setViewMode] = useState<GraphViewMode>("workspace");

	const renderWorkspace = () => {
		if (!workspaceId) {
			return (
				<div className="flex h-full items-center justify-center">
					<p className="text-sm text-muted">Select a workspace to view</p>
				</div>
			);
		}
		return (
			<WorkspaceView
				workspaceId={workspaceId}
				onKgSelect={onKgSelect}
				onNodeSelect={onNodeSelect}
				expanded={expanded}
			/>
		);
	};

	const renderMainContent = () => {
		if (viewMode === "hierarchy") {
			return <HierarchyView workspaceId={workspaceId} />;
		}
		return renderWorkspace();
	};

	const renderHeaderControls = (isFullscreenHeader = false) => (
		<div className="flex items-center gap-2">
			<GraphViewModeTabs viewMode={viewMode} onChange={setViewMode} />
			<button
				type="button"
				onClick={() => setIsFullscreen(!isFullscreen)}
				className="rounded-lg border border-subtle p-2 text-muted soft-hover transition-colors"
				aria-label={isFullscreenHeader ? "Exit fullscreen" : "Fullscreen"}
			>
				{isFullscreenHeader ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
			</button>
		</div>
	);

	if (isFullscreen) {
		return (
			<div className="fixed inset-0 z-50 flex flex-col surface-alt text-primary">
				<div className="flex items-center justify-end border-b border-strong surface-alt px-6 py-3">
					{renderHeaderControls(true)}
				</div>
				<div className="flex-1 overflow-hidden">{renderMainContent()}</div>
			</div>
		);
	}

	return (
		<div className="flex h-full flex-1 flex-col border-r border-strong surface text-primary">
			<div className="flex items-center justify-end border-b border-strong surface-alt px-4 py-3">
				{renderHeaderControls(false)}
			</div>
			<div className="flex-1 overflow-hidden">{renderMainContent()}</div>
		</div>
	);
};

export default CenterGraphPanel;
